import { useEffect, useCallback, useState } from "react";
import type { CardItem } from "../../interfaces/componentInterfaces/cards";
import Cards from "../cards/Cards";
import PageHeader from "../pageHeader/PageHeader";
import Table from "../table/Table";
import { useFiltersStore } from "../../stores/filters.store";
import { useStatisticsStore } from "../../stores/statistics.store";
import { useJobsStore } from "../../stores/jobs.store";
import { useResources } from "../../hooks/useResources";
import { useToast } from "../../contexts/ToastContext";
import { exportJobsToCSV } from "../../services/jobs.service";
import { globalConfig } from "../../config/globalConfig";
import "./UpdatedQuestionsLandingPage.scss";

const UpdatedQuestionsLandingPage = () => {
  const { getResourceValue } = useResources();
  const { showSuccess, showError } = useToast();
  const [isCSVQueued, setIsCSVQueued] = useState(false);

  // Use separate stores
  const {
    selectedCountryIsoCode,
    selectedGradeId,
    selectedLanguageIsoCode,
    selectedSubjectId,
    selectedPendingGraphics,
    searchText,
    searchType,
    currentPage,
    pageSize,
    resetFilters,
  } = useFiltersStore();

  const {
    statistics,
    isLoading: isLoadingStatistics,
    load: loadStatistics,
  } = useStatisticsStore();

  const {
    jobs,
    pagination,
    isLoading: isLoadingJobs,
    load: loadJobs,
  } = useJobsStore();

  // Load statistics when filters change
  useEffect(() => {
    loadStatistics(selectedGradeId, selectedSubjectId, selectedCountryIsoCode, selectedLanguageIsoCode, "update");
  }, [selectedGradeId, selectedSubjectId, selectedCountryIsoCode, selectedLanguageIsoCode, loadStatistics]);

  // Load jobs when filters or pagination change
  useEffect(() => {
    loadJobs({
      page_number: currentPage,
      page_size: pageSize,
      grade_id: selectedGradeId,
      subject_id: selectedSubjectId,
      country_iso_code: selectedCountryIsoCode,
      language_iso_code: selectedLanguageIsoCode,
      pending_graphics: selectedPendingGraphics,
      search_text: searchText,
      search_type: searchType,
      job_type: "update",
    });
    // Re-enable CSV button when filters change
    setIsCSVQueued(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    selectedGradeId,
    selectedSubjectId,
    selectedCountryIsoCode,
    selectedLanguageIsoCode,
    selectedPendingGraphics,
    loadJobs,
  ]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    // Reset all filters to initial state
    resetFilters();
    
    // Load statistics and jobs with default filters (null values)
    loadStatistics(null, null, null, null, "update");
    loadJobs({
      page_number: globalConfig.defaultPageNumber,
      page_size: globalConfig.defaultPageSize,
      grade_id: null,
      subject_id: null,
      country_iso_code: null,
      language_iso_code: null,
      pending_graphics: null,
      search_text: null,
      search_type: null,
      job_type: "update",
    });
  }, [resetFilters, loadStatistics, loadJobs]);

  // CSV Download handler - queues CSV export request
  const handleCSVDownload = useCallback(async () => {
    if (isCSVQueued) return; // Prevent multiple clicks while queued

    try {
      // Prepare export parameters (without pagination)
      const params = {
        grade_id: selectedGradeId || undefined,
        subject_id: selectedSubjectId || undefined,
        country_iso_code: selectedCountryIsoCode || undefined,
        language_iso_code: selectedLanguageIsoCode || undefined,
        pending_graphics: selectedPendingGraphics !== null ? selectedPendingGraphics : undefined,
        search_text: searchText || undefined,
        job_type: "update" as const,
      };

      // Call export API to queue the CSV generation
      await exportJobsToCSV(params);
      
      // Show success toast
      showSuccess(
        getResourceValue("csv_export_queued", "CSV Export Queued"),
        getResourceValue("csv_export_queued_message", "Your CSV export request has been added to the queue. You will receive an email when it's ready.")
      );
      
      // Disable the button
      setIsCSVQueued(true);
    } catch (error) {
      console.error('Error queuing CSV export:', error);
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("csv_export_failed", "Failed to queue CSV export. Please try again.")
      );
    }
  }, [
    isCSVQueued,
    selectedGradeId,
    selectedSubjectId,
    selectedCountryIsoCode,
    selectedLanguageIsoCode,
    selectedPendingGraphics,
    searchText,
    showSuccess,
    showError,
    getResourceValue,
  ]);

  // Convert statistics to cards
  const dashboardCards: CardItem[] = statistics
    ? [
        {
          id: "1",
          title: getResourceValue("total_questions_updated", "Total Questions Updated"),
          value: statistics.total_questions,
        },
        {
          id: "2",
          title: getResourceValue("questions_succeed", "Questions Succeed"),
          value: statistics.succeeded_questions,
        },
        {
          id: "3",
          title: getResourceValue("questions_failed", "Questions Failed"),
          value: statistics.failed_questions,
        },
        {
          id: "4",
          title: getResourceValue("questions_pending_graphics", "Questions Pending Graphics"),
          value: statistics.pending_graphics_questions,
        },
        {
          id: "5",
          title: getResourceValue("questions_published", "Questions Published"),
          value: statistics.published_questions,
        },
      ]
    : [];

  return (
    <div className="page">
      <PageHeader onRefresh={handleRefresh} />
      
      {isLoadingStatistics ? (
        <div className="loading-cards">{getResourceValue("loading_statistics", "Loading statistics...")}</div>
      ) : (
        <Cards cards={dashboardCards} />
      )}
      
      <Table 
        jobs={jobs}
        pagination={pagination}
        isLoading={isLoadingJobs}
        onCSVDownload={handleCSVDownload}
        title={getResourceValue("all_updated_questions", "All Updated Questions")}
        uploadFilesLabel={getResourceValue("update_jobs", "Update Jobs")}
        uploadFilesPath="/update-jobs"
        isCSVDisabled={isCSVQueued}
      />
    </div>
  );
};

export default UpdatedQuestionsLandingPage;
